import { useCallback, useState } from 'react';
import { Input } from '@/components/_untitled/base/input/input';
import { CheckCircle, Mail01 } from '@untitledui/icons';
import { FeaturedIcon } from '@/components/_untitled/foundations/featured-icon/featured-icon';
import { GoogleLogo } from '@/components/_untitled/base/buttons/social-logos';
import type { KycStepProps } from '../../config/kycSteps.config';
import { authApi } from '@/api/authApi';
import { StepFooter } from '../StepFooter';
import { prefixes, useTypedTranslation } from "@/locales";
import { Sentry } from '@/sentry';

export function ForgotPasswordStep({ data, onUpdate, onBack }: KycStepProps) {
    const [tForgotPassword] = useTypedTranslation(prefixes('kyc.steps.forgotPassword'));
    const [tAuthMethod] = useTypedTranslation(prefixes('kyc.steps.authMethod'));
    const [email, setEmail] = useState(data.email || '');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [isOAuthOnly, setIsOAuthOnly] = useState(false);

    const canProceed = !!email && email.includes('@');

    const handleSubmit = useCallback(async () => {
        if (!email) return;

        setIsSubmitting(true);
        try {
            const response = await authApi.forgotPassword(email);
            if (response.isOAuthOnly) {
                setIsOAuthOnly(true);
            }
            setSubmitted(true);
            onUpdate({ email });
        } catch (error) {
            // Always show success for security (don't reveal if email exists)
            Sentry.captureException(error, { tags: { section: 'forgot-password' } });
            setSubmitted(true);
            onUpdate({ email });
        } finally {
            setIsSubmitting(false);
        }
    }, [email, onUpdate]);

    if (submitted) {
        return (
            <>
                <div className="flex flex-col items-center gap-6 py-6">
                    {isOAuthOnly ? (
                        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-gray-50 border border-gray-100">
                            <GoogleLogo colorful className="w-7 h-7" />
                        </div>
                    ) : (
                        <FeaturedIcon icon={ CheckCircle } color="success" theme="light" size="xl"/>
                    )}

                    <div className="flex flex-col items-center gap-2">
                        <h2 className="text-display-sm font-normal text-primary text-center">
                            { isOAuthOnly ? tAuthMethod('useGoogleTitle') : tForgotPassword('successTitle') }
                        </h2>
                        <p className="text-md text-tertiary text-center whitespace-pre-line">
                            { isOAuthOnly ? tAuthMethod('useGoogleMessage') : tForgotPassword('successMessage') }
                        </p>
                    </div>
                </div>
                <StepFooter
                    primary={{
                        label: tForgotPassword('done'),
                        onClick: onBack || (() => {})
                    }}
                    back={{ show: false }}
                />
            </>
        );
    }

    return (
        <>
            <div className="flex flex-col items-center gap-6">
                <FeaturedIcon icon={ Mail01 } color="brand" theme="light" size="xl"/>

                <div className="flex flex-col items-center gap-2 w-full">
                    <h2 className="text-display-sm font-normal text-primary text-center">
                        { tForgotPassword('title') }
                    </h2>
                    <p className="text-md text-tertiary text-center whitespace-pre-line">
                        { tForgotPassword('description') }
                    </p>
                </div>

                <Input
                    type="email"
                    label={ tForgotPassword('email') }
                    placeholder="name@example.com"
                    value={ email }
                    onChange={ setEmail }
                    icon={ Mail01 }
                    aria-label={ tForgotPassword('email') }
                    isRequired
                    autoFocus
                    className="w-full"
                />
            </div>

            <StepFooter
                primary={{
                    label: tForgotPassword('submit'),
                    onClick: handleSubmit,
                    disabled: !canProceed,
                    loading: isSubmitting
                }}
                back={{ show: true, onClick: onBack }}
            />
        </>
    );
}
